<template>
	<div
		ref="containerElement"
		:groupId="attributes.node === 'group' ? attributes.id : null"
		v-show="isActive && isContainerVisible"
		:group="attributes.node === 'group' || null"
		:class="['xone-frame', attributes.keepAspectRatio && 'keep-aspect-ratio', attributes.framebox && 'xone-frame-borders']"
		:xone-name="(attributes.headerContents?attributes.headerContents:attributes.name)"
		:style="{
			// Variables
			'--border-width': attributes.borderWidth,
			'--border-color': attributes.borderColor,
			'--elevation': `${attributes.elevation}px`,
			'--frame-align-column': attributes.align.column,
			// Margins
			marginTop: margins.top + 'px',
			marginRight: margins.right + 'px',
			marginBottom: margins.bottom + 'px',
			marginLeft: margins.left + 'px',
			// Paddings
			paddingTop: paddings.top + 'px',
			paddingRight: paddings.right + 'px',
			paddingBottom: paddings.bottom + 'px',
			paddingLeft: paddings.left + 'px',
			// Size
			height:
				(attributes.node === 'group' && !attributes.drawerOrientation && !attributes.fixed && !isContents && '100%') ||
				//(controlHeight && (attributes.node !== 'group' || attributes.fixed) && `${controlHeight}px`) ||
				(controlHeight&&`${controlHeight}px`)||
				'auto',
			width: (attributes.node === 'group' && !attributes.drawerOrientation && '100%') || (controlWidth && `${controlWidth}px`) || 'auto',
			maxHeight: !isContents && attributes.node === 'group' && containerHeight ? `${containerHeight}px` : null,
			maxWidth: !isContents && attributes.node === 'group' && containerWidth ? `${containerWidth}px` : null,
			minHeight: attributes.minHeight ? attributes.minHeight : null,
			minWidth: attributes.minWidth ? attributes.minWidth : null,
			// Expanded
			flexGrow: attributes.node === 'frame' && attributes.width === 'grow' ? 1 : null,
			flexShrink: attributes.node === 'frame' && attributes.width === 'grow' ? 1 : null,
			// Background
			backgroundColor: displayScreenThresholds ? '#00ff0025' : attributes.bgColor,
			backgroundImage: attributes.image && `url(${appPath}/icons/${attributes.image})`,
			// Border
			border: displayScreenThresholds && `1px solid ${attributes.borderColor}`,
			borderRadius: attributes.borderCornerRadius,
			// Align
			justifyContent: attributes.align.column,
			// Scrolls
			overflowY:
				attributes.node === 'frame' && !attributes.scroll
					? 'hidden'
					: // Esto en android no permite el scroll en un grupo, pero yo lo voy a dejar libre porque me parece que no tiene sentido -> //attributes.node === 'group' ? 'hidden' :
					attributes.scroll && attributes.scrollOrientation && attributes.scrollOrientation !== 'vertical'
					? 'hidden'
					: 
					(attributes.scroll ? 'auto' : 'hidden'),
			overflowX:
				attributes.node === 'frame' && !attributes.scroll
					? 'hidden'
					: attributes.scroll && attributes.scrollOrientation && attributes.scrollOrientation !== 'horizontal'
					? 'hidden'
					:
					(attributes.scroll ? 'auto' : 'hidden'),
			// Floating
			position: attributes.floating.floating && 'absolute',
			zIndex: attributes.floating.floating && '9',
			top: attributes.floating.floating && floatingTop && floatingTop,
			left: attributes.floating.floating && floatingLeft && floatingLeft,
			// Designer
			cursor: isDesigner && isDragging && 'move', //|| (attributes.onClick && 'pointer'),
		}"
		@scroll="onScroll"
		@click="onClick($event)"
		@mousedown="isDesigner && onMouseDown($event)"
		@mouseenter="isDesigner && onMouseEnter($event)"
		@mouseleave="isDesigner && onMouseLeave($event)"
		@mousemove="isDesigner && onDragOver($event)"
		@touchmove="isDesigner && onDragOver($event)"
	>
		<!-- No renderizamos ningún objeto hijo si el padre tiene pendiente hacerse reactivo -->
		<!-- <template v-if="objectInfo.isReactivePending !== true"> -->
		<template v-if="objectInfo.isReactivePending !== true && (attributes.node === 'group' || isContainerVisible)">
			<!-- Rows of the container -->
			<Row
				xv-show="attributes.node === 'group' || isContainerVisible"
				v-for="(row, iRow) in rows"
				:xone-parent="attributes.name"
				:key="`${attributes.name}-${iRow}`"
				:style="{
					// Align
					justifyContent: attributes.align.row,
					alignItems: attributes.align.column,
					flexWrap: attributes.wrap ? 'wrap' : null,
				}"
			>
				<!-- Columns of the Row -->
				<Column
					v-for="(column, iColumn) in row"
					:key="`${column.attributes.name}-${iColumn}`"
					:attributes="column.attributes"
					:xoneDataObject="xoneDataObject"
				>
					<!-- Container -->
					<Container
						v-if="column.attributes.node === 'frame' || column.attributes.node === 'group'"
						:xoneDataObject="xoneDataObject"
						:control="column"
						:containerHeight="controlHeight || controlGrowHeight || containerHeight"
						:containerWidth="controlWidth || controlGrowWidth || containerWidth"
					></Container>
					<!-- Prop -->
					<Prop
						v-if="column.attributes.node === 'prop'"
						:isContentDisableEdit="isContentDisableEdit"
						:xoneDataObject="xoneDataObject"
						:control="column"
						:containerHeight="controlHeight || controlGrowHeight || containerHeight"
						:containerWidth="controlWidth || controlGrowWidth || containerWidth"
					></Prop>
				</Column> </Row
		></template>
	</div>
</template>

<script>
import { computed, inject, onMounted, ref, Ref, ComputedRef, watch, provide, nextTick, PropType, onUnmounted } from "vue";
import Row from "./Row.vue";
import Column from "./Column.vue";
import Prop from "./Prop.vue";
import { xoneAttributesHandler, ContainerAttributes, Margins } from "../composables/XoneAttributesHandler";
import { XoneDataObject } from "../composables/appData/core/XoneDataObject";
import { XoneControl, XoneView } from "../composables/XoneViewsHandler";
import dragAndDrop from "../composables/DragAndDrop";
import AppDataHandler, { Objectinfo } from "../composables/AppDataHandler";
import { getAppPath } from "../composables/helperFunctions/ImageHelper";

export default {
	name: "Container",
	components: { Row, Column, Prop },
	props: {
		/**
		 * xoneDataObject
		 * @type {PropType<XoneDataObject>}
		 */
		xoneDataObject: { type: Object, required: true },
		control: { type: Object, required: true },
		containerWidth: { type: Number, default: 0 },
		containerHeight: { type: Number, default: 0 },
		visibilityBit: { type: Number, default: 1 },
	},
	emits: ["onDataObjectChange"],
	/**
	 * Group / Frame control
	 */
	setup(props, context) {
		const displayScreenThresholds = inject("displayScreenThresholds");

		/**
		 * objectInfo
		 * @type {Objectinfo}
		 */
		const objectInfo = inject("objectInfo");
		const { isContents } = objectInfo;

		// Si es un contents y es edit-inrow, hacemos el objeto reactivo y lo creamos en el stack de objetos como una vista, por si se desea coger la vista del objeto
		// if (objectInfo.editInRow && props.control.attributes.node === "group") {
		if (objectInfo.isReactivePending?.value && props.control.attributes.node === "group") {
			// Clonamos el objeto para que no pierda su reactividad
			const newObj = props.xoneDataObject.clone().doModelReactive();
			// Hacemos push del objeto en el stack de objetos
			const contentsRowbreadcrumbId = AppDataHandler.pushXoneDataObject(newObj, "contentsRow", false, false, true);
			// // Creamos y proveemos la vista
			// const newXoneView = new XoneView(newObj);
			// provide("xoneView", newXoneView);
			// Destruimos el objeto del stack de objetos
			onUnmounted(() => AppDataHandler.removeBreadcrumb(contentsRowbreadcrumbId));
			// Emitimos el cambio de objeto para setearlo en el contents
			context.emit("onDataObjectChange", newObj);
		}

		/**
		 * Attributes model
		 * @type {ComputedRef<ContainerAttributes>}
		 */
		const attributesModel = computed(() => xoneAttributesHandler.getContainerAttributes(props.control.attributes));

		/**
		 * Reactive Attributes
		 * @type {Ref<ContainerAttributes>}
		 */
		const attributes = ref({ ...attributesModel.value });

		// Observe changes in attributes model and data model with ##FLD_ fields and fetch them to attributes
		xoneAttributesHandler.watchAttributes(attributes, attributesModel, props.xoneDataObject.model);

		// Provide group id
		if (attributes.value.node === "group") provide("groupId", attributes.value.id);

		// Provide isDrawer
		const isDrawer = attributes.value.drawerOrientation === "left" || attributes.value.drawerOrientation === "right";
		provide("isDrawer", isDrawer);

		/**
		 * Check if disablevisible
		 * @type {ComputedRef<boolean>}
		 */
		const isDisableVisible = computed(() => {
			// Frame without children
			if (attributes.value.node === "frame" && props.control.controls?.length === 0) return true;
			// No disablevisible attribute
			if ((attributes.value.disableVisible ?? "") === "") return false;
			// Eval disablevisible formula
			return xoneAttributesHandler.evalFormula(attributes.value.disableVisible, props.xoneDataObject?.model);
		});
		/**
		 * Gestionamos los timeouts de las animaciones con respecto al disablevisible para hacer o no visible el contenedor
		 * @type {Ref<boolean>}
		 */
		const isContainerVisible = ref();
		
		const isContentDisableEdit = ref();

		/**
		 * Group active
		 * @type {{activeGroup:Ref<string>}}
		 */
		const { activeGroup } = inject("groupHandler");

		/**
		 * Is current group active
		 * @type {ComputedRef<boolean>}
		 */
		const isActive = computed(() => {
			return (
				attributes.value.node !== "group" ||
				attributes.value.fixed ||
				attributes.value.id === activeGroup.value ||
				attributes.value.drawerOrientation ||
				props.visibilityBit === 4
			);
		});

		// Execute onfocus method
		if (attributes.value.node === "group" && attributes.value.onFocus)
			watch(
				() => activeGroup.value,
				async (newValue) => {
					if (newValue !== attributes.value.id) return;
					await xoneAttributesHandler.executeMethod(attributes.value.onFocus, props.xoneDataObject);
				}
			);

		/**
		 * Container rows / columns
		 * @type {ComputedRef<Array>}
		 */
		const rows = computed(() => {
			const validNodes = ["group", "frame", "prop"];
			let rowsArr = [];
			let row = [];
			props.control.controls
				?.filter((e) => validNodes.includes(e.attributes.node))
				?.forEach((e) => {
					// Set width and height = 100% if group has only 1 child and it's a frame
					if (
						attributes.value.node === "group" &&
						props.control.controls.length === 1 &&
						e.attributes.node === "frame" &&
						!attributes.value.fixed &&
						!attributes.value.floating?.floating &&
						!attributes.value.floating?.drawerOrientation
					) {
						if (!objectInfo.isMsgBox && !isContents) {
							// Anddroid obliga a que exista un frame dentro del group, y lo pone tamaño del 100%, yo respetaré si viene tamaño del usuario
							if (!e.attributes.height) e.attributes.height = "100%";
							if (!e.attributes.width) e.attributes.width = "100%";
						}
					}
					const controlAttributes = xoneAttributesHandler.getContainerAttributes(e.attributes);
					// newline true
					if (controlAttributes.newLine) {
						if (row.length !== 0) {
							rowsArr.push(row);
							row = [];
						}
						row.push(e);
					}
					// newline false
					else {
						row.push(e);
					}
				});
			rowsArr.push(row);

			// Exclude empty containers
			/** @type {boolean} */
			let clear;
			const clearEmptyContainers = (control, /** @type {Array} */ parent) => {
				if (
					control.attributes.node === "frame" &&
					(!control.controls || control.controls.filter((e) => validNodes.includes(e.attributes.node)).length === 0)
				) {
					parent.splice(parent.indexOf(control), 1);
					clear = true;
				}
				control.controls?.forEach((e) => clearEmptyContainers(e, control.controls));
			};
			do {
				clear = false;
				rowsArr.forEach((row) => row.forEach((column) => clearEmptyContainers(column, row)));
			} while (clear);

			return rowsArr;
		});

		const isMouseOver = ref(false);

		/**
		 * Scale Factor
		 * @type {{widthFactor: ComputedRef<number>, heightFactor: ComputedRef<number>}}
		 */
		const { widthFactor, heightFactor } = inject("scaleFactor");

		const controlGrowWidth = ref(null),
			controlGrowHeight = ref(null);

		//
		// Width|Height = -1; 'grow', the container occuppies all available size
		onMounted(async () => {
			if (isContents) objectInfo.rowInfo.setIsLoaded(true);

			// Set flex-grow to row container -> Height Grow
			if (attributes.value.height === "grow") {
				const { parentElement } = containerElement.value;
				if (parentElement.classList.contains("xone-row")) {
					parentElement.style.flexGrow = 1;
					parentElement.style.flexShrink = 1;
					parentElement.style.overflow = "hidden";
					const observerHeight = new ResizeObserver(() => {
						controlGrowHeight.value = 1;
						nextTick(() => (controlGrowHeight.value = parentElement.clientHeight - (margins.value?.top || 0) - (margins.value?.bottom || 0)));
						fixJustifyCenter();
					});
					observerHeight.observe(parentElement);
					onUnmounted(() => observerHeight?.disconnect());
				}
			}

			if (attributes.value.width === "grow" || attributes.value.align.column === "center") {
				const observerWidth = new ResizeObserver(() => {
					if (attributes.value.width === "grow") {
						controlGrowWidth.value = 1;
						nextTick().then(
							() => (controlGrowWidth.value = containerElement.value.clientWidth - (margins.value?.left || 0) - (margins.value?.right || 0))
						);
					}
					fixJustifyCenter();
				});
				observerWidth.observe(containerElement.value);
				onUnmounted(() => observerWidth?.disconnect());
			}
		});

		/**
		 * Calculate width of the current container
		 * @type {ComputedRef<number>}
		 */
		const controlWidth = computed(() => {
			let width = xoneAttributesHandler.getScaledSize(attributes.value.width, props.containerWidth, widthFactor.value);
			return !width ? null : isMouseOver.value ? width - 4 : width;
		});

		/**
		 * Calculate height of the current container
		 * @type {ComputedRef<number>}
		 */
		const controlHeight = computed(() => {
			const height = xoneAttributesHandler.getScaledSize(attributes.value.height, props.containerHeight, heightFactor.value);
			if (!height && controlGrowHeight.value) return controlGrowHeight.value;
			return !height ? null : isMouseOver.value ? height - 4 : height;
		});

		/**
		 * Margins of the current container
		 * @type {ComputedRef<Margins>}
		 */
		const margins = computed(() => ({
			top: xoneAttributesHandler.getScaledSize(attributes.value.margins.top, props.containerHeight, heightFactor.value),
			left: xoneAttributesHandler.getScaledSize(attributes.value.margins.left, props.containerWidth, widthFactor.value),
			bottom: xoneAttributesHandler.getScaledSize(attributes.value.margins.bottom, props.containerHeight, heightFactor.value),
			right: xoneAttributesHandler.getScaledSize(attributes.value.margins.right, props.containerWidth, widthFactor.value),
		}));

		/**
		 * Margins of the current container
		 * @type {ComputedRef<Margins>}
		 */
		const paddings = computed(() => ({
			top: xoneAttributesHandler.getScaledSize(attributes.value.paddings.top, props.containerHeight, heightFactor.value),
			left: xoneAttributesHandler.getScaledSize(attributes.value.paddings.left, props.containerWidth, widthFactor.value),
			bottom: xoneAttributesHandler.getScaledSize(attributes.value.paddings.bottom, props.containerHeight, heightFactor.value),
			right: xoneAttributesHandler.getScaledSize(attributes.value.paddings.right, props.containerWidth, widthFactor.value),
		}));

		/**
		 * calculate floating top
		 * @type {ComputedRef<string>}
		 */
		const floatingTop = computed(() => xoneAttributesHandler.getScaledPosition(attributes.value.floating.top, heightFactor.value));

		/**
		 * calculate floating leeft
		 * @type {ComputedRef<string>}
		 */
		const floatingLeft = computed(() => xoneAttributesHandler.getScaledPosition(attributes.value.floating.left, widthFactor.value));

		//
		// Handler refresh
		/**
		 * xoneView
		 * @type {XoneView}
		 */
		const xoneView = inject("xoneView");

		onMounted(() => {
			// Create XoneControl
			const xoneControl = new XoneControl(attributes.value.name, containerElement.value);
			// refresh method
			xoneControl.refresh = () =>
				props.control.controls.forEach((e) => {
					if (xoneView[e.attributes.name]?.refresh) xoneView[e.attributes.name].refresh();
				});
			// scrollBy method
			xoneControl.scrollBy = (value) => {
				if (!containerElement.value) return;
				value = value.toString();
				const { scrollHeight } = containerElement.value;
				if (value.includes("%"))
					return containerElement.value.scrollTo({
						top: Number((value.replace("%", "") * scrollHeight) / 100),
						behavior: "smooth",
					});
				containerElement.value.scrollTo({
					top: Number(value.replace("p", "") * heightFactor.value),
					behavior: "smooth",
				});
			};
			// scrollTo method
			xoneControl.scrollTo = xoneControl.scrollBy;
			// scrollToTop method
			xoneControl.scrollToTop = () => xoneControl.scrollBy(0);
			// scrollToBottom method
			xoneControl.scrollToBottom = () => xoneControl.scrollBy(containerElement.value?.scrollHeight);
			// Add control to view
			xoneView.addControl(xoneControl);

			// Agregamos el valor inicial al isContainerVisible
			isContainerVisible.value = !isDisableVisible.value;
			// Gestionamos las animaciones
			watch(
				() => isDisableVisible.value,
				(value) => {
					// Animation In
					if (!value && !containerElement.value.style.animation && attributes.value.animationIn)
						containerElement.value.style.animation = attributes.value.animationIn;
					// Animation Out
					if (value && attributes.value.animationOut) xoneAttributesHandler.doAnimationOut(containerElement.value, attributes.value);
					// Seteamos el nuevo valor a isContainerVisible
					setTimeout(() => (isContainerVisible.value = !value), attributes.value.animationIn || attributes.value.animationOut ? 300 : 0);
				}
			);
		});

		/**
		 * containerElement
		 * @type {Ref<HTMLElement>}
		 */
		const containerElement = ref();

		// Flex no se comporta bien haciendo un justify-center center cuando tiene scroll, compruebo si lo tiene y se lo quito en tal caso
		const fixJustifyCenter = () => {
			if (attributes.value.align.column !== "center") return;
			const doFix = () => {
				if (!containerElement.value) return;
				if (containerElement.value.offsetHeight < containerElement.value.scrollHeight) containerElement.value.style.justifyContent = null;
			};
			doFix();
			[...Array(5).keys()].forEach((e) => setTimeout(() => doFix(), e * 25));
		};

		nextTick(() => fixJustifyCenter());

		watch(
			() => props.containerHeight,
			() => fixJustifyCenter()
		);

		//
		// onScroll event
		let scrollTimeout;
		const onScroll = () => {
			if (scrollTimeout) clearTimeout(scrollTimeout);
			scrollTimeout = setTimeout(() => xoneAttributesHandler.onScrollEvent(containerElement.value, attributes.value, props.xoneDataObject), 250);
		};
		nextTick(() => onScroll());

		//
		// on click button
		let isExecutingOnClick = false;
		const onClick = async (/** @type {PointerEvent} */ e) => {
			if (isExecutingOnClick || !attributes.value.onClick) return;
			e.stopPropagation();
			isExecutingOnClick = true;
			await xoneAttributesHandler.onClick(attributes.value, props.xoneDataObject, objectInfo);
			isExecutingOnClick = false;
		};

		return {
			objectInfo,
			containerElement,
			displayScreenThresholds,
			attributes,
			isActive,
			rows,
			isMouseOver,
			controlWidth,
			controlHeight,
			margins,
			paddings,
			isDisableVisible,
			floatingTop,
			floatingLeft,
			isDesigner: dragAndDrop.getIsDesigner(),
			isDragging: dragAndDrop.getIsDragging(),
			// isDesignerPropSelected: computed(
			//   () => dragAndDrop.getSelectedControl()?.value === containerElement.value
			// ),
			borderDesignSelected: dragAndDrop.getBorderSelected(),
			borderDesignOver: dragAndDrop.getBorderContainerOver(),
			controlGrowWidth,
			controlGrowHeight,
			onScroll,
			onClick,
			isContents,
			isContainerVisible,
			appPath: getAppPath(),
			//
			// Designer mode Drag & Drop
			onMouseEnter: () => dragAndDrop.mouseEnter(containerElement.value),
			onMouseLeave: () => dragAndDrop.mouseLeave(containerElement.value),
			onMouseDown: (e) => dragAndDrop.mouseDown(e),
			onDragOver: (e) => dragAndDrop.mouseMove(e),
		};
	},
};
</script>
<style scoped>
.xone-frame {
	display: flex;
	flex-direction: column;
	align-self: flex-start;
	background-repeat: no-repeat;
	background-position: center;
	/* background-size: contain; */
	background-size: 100% 100%;
	overflow-x: hidden;
	overflow-y: auto;
	/* transition: all 0.2s; */
	box-sizing: border-box;
	-moz-box-sizing: border-box;
	-webkit-box-sizing: border-box;

	box-shadow: 0px 0px var(--elevation) 0 rgba(0, 0, 0, 0.2);

	outline: var(--outine-border);
	outline-offset: var(--selected-offset);
}

.xone-frame-borders {
	border: var(--border-width) solid var(--border-color);
}

.keep-aspect-ratio {
	background-size: contain;
}

.tabcontent {
	height: auto;
	width: auto;
	flex-shrink: 1;
	flex-grow: 1;
}
</style>
